import { useCallback, useEffect, useState } from 'react';
import { Space, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import CardWithIcon from 'components/new-ui/base/card/CardWithIcon';
import SelectChargeModal, {
    ChargeSelectOption,
} from 'views/invoices/invoice-details/components/additional-charges/SelectChargeModal';
import EditAdditionalChargeModal from 'views/customers/customer/tabs/additional-charges/EditAdditionalChargeModal';
import useDebouncedValue from 'hooks/useDebouncedValue';
import {
    addCustomerAdditionalCharge,
    archiveCustomerAdditionalCharge,
    listAdditionalCharges,
    listCustomerAdditionalCharges,
    unarchiveCustomerAdditionalCharge,
    updateCustomerAdditionalCharge,
} from 'store/invoice-config/client';
import { ChargeOptionRow, Code, Desc, Nominal, Tax } from 'views/invoices/invoice-details/components/styles';
import EllipsisTextTooltip from 'components/EllipsisTextTooltip';
import { AdditionalCharge, getIrelandTaxCodeLabel, ListAdditionalChargesParams } from 'store/invoice-config/types';
import Notification from 'components/ui/base/notification/Notification';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import { getErrorMessage } from 'utils/getErrorMessage';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import AdditionalChargesTable from 'views/invoices/settings/components/AdditionalChargesTable';
import useInvoiceChargesFilters from 'views/invoices/hooks/useInvoiceChargesFilters';
import Segmented from 'components/new-ui/base/segmented/Segmented';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import ArchiveChargeModal from 'views/invoices/settings/components/ArchiveChargeModal';
import UnarchiveChargeModal from 'views/invoices/settings/components/UnarchiveChargeModal';
import { getCurrencyData } from 'utils/common';
import { usePartyContext } from '../../PartyProvider';

enum Modals {
    SELECT_CHARGE = 'select-charge',
    UPDATE_CHARGE = 'update-charge',
    ARHIVE_CHAGE = 'archive-charge',
    UNARCHIVE_CHARGE = 'unarchive-charge',
}

const AdditionalChargesTab = () => {
    const { customerId } = useParams();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { canUpdate, viewOnly } = usePartyContext();

    const [modal, setModal] = useState<Modals | null>(null);
    const [selectedCharge, setSelectedCharge] = useState<AdditionalCharge | null>(null);
    const [query, setQuery] = useState<string>();
    const [tenantLevelCharges, setTenantLevelCharges] = useState<AdditionalCharge[]>([]);
    const [archivedTenantLevelProductCodes, setArchivedTenantLevelProductCodes] = useState<Set<string>>(new Set());

    const { currencySymbol } = getCurrencyData();
    const debouncedQuery = useDebouncedValue(query);

    const {
        data: customerCharges,
        isLoading,
        refetch,
        handleTableChange,
        pagination,
        appliedParams,
        queryInputVal,
        setArchived,
        setQueryInputVal,
    } = useInvoiceChargesFilters(
        useCallback(
            (params: ListAdditionalChargesParams) => listCustomerAdditionalCharges(customerId!, params),
            [customerId]
        )
    );

    const { archived, sort, sortDirection } = appliedParams;
    const canTriggerUpdates = !viewOnly && canUpdate;

    const closeModal = () => {
        setModal(null);
        setSelectedCharge(null);
    };

    const handleAddCustomerCharge = async (selected: ChargeSelectOption) => {
        if (!selected.id || !customerId) {
            console.error('Missing charge id or customer id');
            return Notification({
                type: 'error',
                messageTitle: 'Operation Failed.',
                description: DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        }

        try {
            showGlobalOverlay({ type: 'LoadingOverlay' });

            await addCustomerAdditionalCharge(customerId, selected.id);
            refetch();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                type: 'error',
                messageTitle: 'Operation Failed.',
                description: errorMessage || DEFAULT_INTERNAL_ERROR_MESSAGE,
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const handleEditAdditionalCharge = async (values: { amount: number; percentage: number | null }) => {
        if (!customerId || !selectedCharge?.id) return;

        await updateCustomerAdditionalCharge(customerId, selectedCharge.id, {
            productCode: selectedCharge.productCode,
            description: selectedCharge.description,
            nominalCode: selectedCharge.nominalCode,
            taxCode: selectedCharge.taxCode,
            amount: values.amount,
            percentage: values.percentage ?? undefined,
        });

        Notification({
            type: 'success',
            messageTitle: 'Update Complete',
            description: 'Customer additional charge successfully updated.',
        });

        refetch();
        closeModal();
    };

    const handleArchive = async () => {
        if (!customerId || !selectedCharge?.id) return;

        try {
            showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Archiving additional charge' } });

            await archiveCustomerAdditionalCharge(customerId, selectedCharge.id);

            await refetch();
            closeModal();

            Notification({
                type: 'success',
                messageTitle: 'Operation Successful',
                description: 'Additional charge archived successfully.',
                placement: 'top',
            });
        } catch (error) {
            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const handleUnarchive = async () => {
        if (!customerId || !selectedCharge?.id) return;

        try {
            showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Unarchiving additional charge' } });

            await unarchiveCustomerAdditionalCharge(customerId, selectedCharge.id);

            await refetch();
            closeModal();

            Notification({
                type: 'success',
                messageTitle: 'Operation Successful',
                description: 'Additional charge unarchived successfully.',
                placement: 'top',
            });
        } catch (error) {
            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    useEffect(() => {
        if (modal !== Modals.SELECT_CHARGE) return;

        listAdditionalCharges({ query: debouncedQuery, archived: false })
            .then((res) => setTenantLevelCharges(res.list))
            .catch(() => setTenantLevelCharges([]));
    }, [debouncedQuery, modal]);

    // Gets the list of archived additional charges to compare them against the customer level charges
    // Refresh the list when we switch the archive filter
    useEffect(() => {
        listAdditionalCharges({ archived: true, size: 1000 })
            .then((res) => setArchivedTenantLevelProductCodes(new Set(res.list.map((charge) => charge.productCode))))
            .catch(() => setArchivedTenantLevelProductCodes(new Set()));
    }, [archived]);

    const tenantLevelChargesOptions: ChargeSelectOption[] =
        tenantLevelCharges.map((el) => {
            return {
                id: el.id,
                value: el.id,
                searchText: `${el.productCode} ${el.description} ${el.taxCode} ${el.nominalCode}`.toLowerCase(),
                rawData: el,
                label: (
                    <ChargeOptionRow>
                        <EllipsisTextTooltip tooltipTitle={el.productCode}>
                            <Code>{el.productCode}</Code>
                        </EllipsisTextTooltip>
                        <EllipsisTextTooltip tooltipTitle={el.description}>
                            <Desc>{el.description}</Desc>
                        </EllipsisTextTooltip>
                        <EllipsisTextTooltip tooltipTitle={el.nominalCode}>
                            <Nominal>{el.nominalCode}</Nominal>
                        </EllipsisTextTooltip>
                        <EllipsisTextTooltip tooltipTitle={getIrelandTaxCodeLabel(el.taxCode)}>
                            <Tax>{getIrelandTaxCodeLabel(el.taxCode)}</Tax>
                        </EllipsisTextTooltip>
                    </ChargeOptionRow>
                ),
            };
        }) ?? [];

    return (
        <>
            <CardWithIcon
                gap="large"
                layout="horizontal"
                title="Additional Charges"
                iconProps={{
                    size: 49,
                    borderRadius: 16,
                    icon: <PlusOutlined />,
                    status: 'info',
                }}
                headerActions={
                    <Space size={8}>
                        <Segmented
                            value={archived ? 'archived' : 'active'}
                            onChange={(val) => setArchived(val === 'active' ? false : true)}
                            options={[
                                {
                                    value: 'active',
                                    label: 'Active',
                                },
                                {
                                    value: 'archived',
                                    label: 'Archived',
                                },
                            ]}
                        />

                        {canTriggerUpdates && (
                            <Button onClick={() => setModal(Modals.SELECT_CHARGE)}>Add Charge</Button>
                        )}
                    </Space>
                }
                children={
                    <Space size={16} direction="vertical" style={{ width: '100%' }}>
                        <Typography.Text>
                            All additional charges that have been set up will be automatically applied on all customer's
                            invoices.
                        </Typography.Text>

                        <SearchBar
                            inputPlaceholder="Search"
                            value={queryInputVal}
                            onSearch={(val) => setQueryInputVal(val)}
                            onClear={() => setQueryInputVal('')}
                        />

                        <AdditionalChargesTable
                            showActions={canTriggerUpdates}
                            currencySymbol={currencySymbol}
                            data={customerCharges?.list ?? []}
                            onChange={handleTableChange}
                            currentSortDirection={sortDirection}
                            currentSortParam={sort}
                            loading={isLoading}
                            pagination={pagination}
                            archivedAtTenantLevelProductCodes={archivedTenantLevelProductCodes}
                            actions={{
                                onEdit: (record) => {
                                    setSelectedCharge(record);
                                    setModal(Modals.UPDATE_CHARGE);
                                },
                                onArchive: (record) => {
                                    setSelectedCharge(record);
                                    setModal(Modals.ARHIVE_CHAGE);
                                },
                                onUnarchive: (record) => {
                                    setSelectedCharge(record);
                                    setModal(Modals.UNARCHIVE_CHARGE);
                                },
                            }}
                        />
                    </Space>
                }
            />

            <SelectChargeModal
                open={modal === Modals.SELECT_CHARGE}
                charges={tenantLevelChargesOptions}
                isLoading={isLoading}
                onSearch={setQuery}
                onCancel={closeModal}
                onConfirm={handleAddCustomerCharge}
                onClear={() => setQuery(undefined)}
            />

            <EditAdditionalChargeModal
                open={modal === Modals.UPDATE_CHARGE}
                onClose={closeModal}
                onConfirm={handleEditAdditionalCharge}
                currencySymbol={currencySymbol}
                initialData={selectedCharge ?? undefined}
            />

            <ArchiveChargeModal open={modal === Modals.ARHIVE_CHAGE} onConfirm={handleArchive} onClose={closeModal} />

            <UnarchiveChargeModal
                open={modal === Modals.UNARCHIVE_CHARGE}
                onConfirm={handleUnarchive}
                onClose={closeModal}
            />
        </>
    );
};

export default AdditionalChargesTab;
